// Revisado: 2025-12-18 [I18n Scan]
import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../../constants';
import { useTranslation } from 'react-i18next';

const SupportHints = ({ slots, activeSkills = [], onAddSkill, allSkills = [] }) => {
    const { t } = useTranslation();
    const [skillsCache, setSkillsCache] = useState({}); // { support_id: { skills: [], normal_effects: [] } }
    const [isLoading, setIsLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);

    const SLOT_COLORS = [
        { bg: 'bg-red-500', text: 'text-red-600', border: 'border-red-200', lightBg: 'bg-red-50' },
        { bg: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-200', lightBg: 'bg-blue-50' },
        { bg: 'bg-emerald-500', text: 'text-emerald-600', border: 'border-emerald-200', lightBg: 'bg-emerald-50' },
        { bg: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-200', lightBg: 'bg-orange-50' },
        { bg: 'bg-violet-500', text: 'text-violet-600', border: 'border-violet-200', lightBg: 'bg-violet-50' },
        { bg: 'bg-cyan-500', text: 'text-cyan-600', border: 'border-cyan-200', lightBg: 'bg-cyan-50' }
    ];

    // 1. Fetch Full Data (Skills + Effects)
    useEffect(() => {
        let isMounted = true;

        const fetchNeededData = async () => {
            const activeSupports = slots.filter(s => s.support).map(s => s.support);

            // Check if we have data for this ID (skills AND effects)
            // We use the same cache structure but check if it's populated
            const neededIds = activeSupports
                .filter(s => s.support_id && !skillsCache.hasOwnProperty(s.support_id))
                .map(s => s.support_id);

            const uniqueNeededIds = [...new Set(neededIds)];

            if (uniqueNeededIds.length === 0) return;

            setIsLoading(true);

            try {
                const results = await Promise.all(uniqueNeededIds.map(async (id) => {
                    try {
                        const response = await fetch(`${API_BASE_URL}/api/supports/${id}`);
                        if (response.ok) {
                            const data = await response.json();
                            // Store full relevant data
                            return {
                                id,
                                data: {
                                    skills: data.skills || [],
                                    normal_effects: data.normal_effects || [],
                                    unique_effects: data.unique_effects || []
                                }
                            };
                        } else {
                            return { id, data: { skills: [], normal_effects: [], unique_effects: [] } };
                        }
                    } catch (err) {
                        return { id, data: { skills: [], normal_effects: [], unique_effects: [] } };
                    }
                }));

                if (isMounted) {
                    setSkillsCache(prev => {
                        const newCache = { ...prev };
                        results.forEach(res => {
                            newCache[res.id] = res.data;
                        });
                        return newCache;
                    });
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchNeededData();
        return () => { isMounted = false; };
    }, [slots, skillsCache]);


    // 2. Aggregate Hints with Logic
    const aggregatedHints = React.useMemo(() => {
        const hintsMap = new Map(); // SkillID -> { skillInfo, providers: [] }

        slots.forEach((slot, index) => {
            if (!slot.support) return;
            const supportId = slot.support.support_id;
            const cacheData = skillsCache[supportId];

            if (!cacheData) return;

            // 1. Get Cap Level (0-4)
            const capLevel = slot.capLevel || 0;

            // 2. Find Bonus Values for this Card at this Cap
            let hintLvVal = 0;
            let hintFreqVal = 0;

            if (cacheData.normal_effects) {
                // "Hint Levels" (Strict)
                const lvEffect = cacheData.normal_effects.find(e => e.Effect === "Hint Levels");
                if (lvEffect && lvEffect.Bonuses) {
                    hintLvVal = lvEffect.Bonuses[capLevel] || 0;
                }

                // "Hint Frequency" (Strict)
                const freqEffect = cacheData.normal_effects.find(e => e.Effect === "Hint Frequency");
                if (freqEffect && freqEffect.Bonuses) {
                    hintFreqVal = freqEffect.Bonuses[capLevel] || 0;
                }
            }

            // 3. Find Unique Bonus Values
            let uniqueLvVal = 0;
            let uniqueFreqVal = 0;

            if (cacheData.unique_effects) {
                // "Hint Levels" (Strict Unique)
                const uniqueLv = cacheData.unique_effects.find(e => e.Effect === "Hint Levels");
                if (uniqueLv) {
                    uniqueLvVal = parseInt(uniqueLv.Bonus || 0, 10);
                }

                // "Hint Frequency" (Strict Unique)
                const uniqueFreq = cacheData.unique_effects.find(e => e.Effect === "Hint Frequency");
                if (uniqueFreq) {
                    uniqueFreqVal = parseInt(uniqueFreq.Bonus || 0, 10);
                }
            }

            // 4. Iterate Skills provided by this card
            if (cacheData.skills) {
                cacheData.skills.forEach(skill => {
                    // Logic Update: Remove default to 1. Allow 0.
                    const normalVal = parseInt(hintLvVal || 0, 10);
                    // Total = Normal + Unique. If Normal is 0, Total is just Unique (or 0).
                    const totalHintLv = normalVal + uniqueLvVal;

                    const normalFreq = parseInt(hintFreqVal || 0, 10);
                    const totalFreq = normalFreq + uniqueFreqVal;

                    const skillId = skill.Id_skill || skill.id_skill;

                    if (!hintsMap.has(skillId)) {
                        hintsMap.set(skillId, {
                            skillInfo: skill,
                            providers: []
                        });
                    }

                    hintsMap.get(skillId).providers.push({
                        slotIndex: index,
                        hintLv: totalHintLv,
                        hintFreq: totalFreq,
                        isUniqueLv: uniqueLvVal > 0,
                        isUniqueFreq: uniqueFreqVal > 0
                    });
                });
            }
        });

        // Convert to array and sort by Skill ID
        return Array.from(hintsMap.values()).sort((a, b) => {
            return (a.skillInfo.Id_skill || 0) - (b.skillInfo.Id_skill || 0);
        });

    }, [slots, skillsCache]);


    const isActive = (skillId) => {
        return activeSkills.some(s => {
            const sId_skill = s.skill.Id_skill || s.skill.skill_id;
            return parseInt(sId_skill, 10) === parseInt(skillId, 10);
        });
    };

    const handleAddHint = (skillInfo, level) => {
        // ... (Same logic as before)
        if (!onAddSkill) return;
        if (!allSkills || allSkills.length === 0) return;

        const hintId = skillInfo.Id_skill || skillInfo.id_skill;
        const fullSkill = allSkills.find(s => parseInt(s.Id_skill, 10) === parseInt(hintId, 10));

        if (fullSkill) {
            onAddSkill(fullSkill, level, level);
        } else {
            // Fallback
            const fallbackSkill = allSkills.find(s => parseInt(s.Id, 10) === parseInt(hintId, 10));
            if (fallbackSkill) onAddSkill(fallbackSkill, level, level);
        }
    };

    // Stats
    const totalSkills = aggregatedHints.length;
    const addedSkills = aggregatedHints.filter(h => isActive(h.skillInfo.Id_skill || h.skillInfo.id_skill)).length;
    const availableSkills = totalSkills - addedSkills;

    if (aggregatedHints.length === 0) return null;

    return (
        <div className="bg-white rounded-xl shadow-sm mt-6 overflow-hidden transition-all duration-300">
            <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors border-b"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-800">{t('SupportHints.title')}</h3>
                    {isLoading && <span className="text-xs text-blue-500 animate-pulse">{t('SupportHints.loading')}</span>}

                    <div className="flex items-center gap-3 ml-4 text-xs font-medium">
                        <span className="text-gray-500">
                            {t('SupportHints.total')} <span className="font-bold text-gray-700">{totalSkills}</span>
                        </span>
                        <span className="hidden w-px h-3 bg-gray-300 sm:block"></span>
                        <span className="text-emerald-600">
                            {t('SupportHints.added')} <span className="font-bold">{addedSkills}</span>
                        </span>
                        <span className="hidden w-px h-3 bg-gray-300 sm:block"></span>
                        <span className="text-blue-600">
                            {t('SupportHints.available')} <span className="font-bold">{availableSkills}</span>
                        </span>
                    </div>
                </div>
                <svg
                    className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {aggregatedHints.map((entry, index) => {
                        const { skillInfo, providers } = entry;
                        const isAdded = isActive(skillInfo.Id_skill || skillInfo.id_skill);

                        return (
                            <div
                                key={`${skillInfo.skill_name}-${index}`}
                                className={`
                                    flex flex-col p-2 rounded-lg border border-gray-100 transition-all
                                    ${isAdded ? 'bg-gray-100 opacity-60 grayscale' : 'bg-gray-50'}
                                `}
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="relative w-10 h-10 shrink-0">
                                        <img
                                            src={`/images/Icon/Skill_Icons/icon_skill_${skillInfo.icon_id}.png`}
                                            alt={skillInfo.skill_name}
                                            className="w-full h-full object-contain"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-700 truncate" title={skillInfo.skill_name}>
                                            {skillInfo.skill_name}
                                        </p>
                                    </div>
                                </div>

                                {/* LABELS AREA (Colored Providers) */}
                                <div className="flex flex-wrap gap-1 mb-2">
                                    {providers.map((p, pIdx) => {
                                        const color = SLOT_COLORS[p.slotIndex % SLOT_COLORS.length];
                                        return (
                                            <div
                                                key={pIdx}
                                                className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center w-fit ${color.lightBg} ${color.border} ${color.text}`}
                                            >
                                                {/* LED DOT */}
                                                <span className={`w-2 h-2 rounded-full mr-1 ${color.bg} shadow-sm`}></span>

                                                <span
                                                    className={`font-bold whitespace-nowrap ${p.isUniqueLv ? 'text-amber-500 font-extrabold' : ''}`}
                                                    title="Hint Level"
                                                >
                                                    Hint Lv {p.hintLv}
                                                </span>
                                                {p.hintFreq > 0 && (
                                                    <span
                                                        className={`opacity-80 ml-1 whitespace-nowrap ${p.isUniqueFreq ? 'text-amber-500 font-extrabold opacity-100' : ''}`}
                                                        title="Hint Frequency"
                                                    >
                                                        ({p.hintFreq}%)
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* ADD BUTTONS */}
                                {!isAdded && (
                                    <div className="mt-auto flex bg-gray-200 rounded-lg p-1 gap-1 w-full">
                                        {[0, 1, 2, 3, 4, 5].map((level) => (
                                            <button
                                                key={level}
                                                onClick={() => handleAddHint(skillInfo, level)}
                                                className={`
                                                    flex-1 px-1 py-1 text-xs font-bold rounded transition-all
                                                    ${level === 5 ? 'text-purple-600' : 'text-gray-600'}
                                                    hover:bg-white hover:shadow-sm hover:text-gray-800
                                                `}
                                            >
                                                {level === 5 ? 'Max' : level}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default SupportHints;
// Revisado: 2025-12-18 I18n Updated
